import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, ArrowLeft, Plus, Users, Gavel, ChevronRight, Trash2, CreditCard, Copy, Check, Upload, FileText, QrCode, AlertCircle, Clock, CheckCircle, Hash, ExternalLink, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";

export default function FestivalPage() {
  const { festivalId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [festival, setFestival] = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(null);
  const [showFestivalPaymentDialog, setShowFestivalPaymentDialog] = useState(false);
  const [copiedIban, setCopiedIban] = useState(false);
  const [copiedEventCode, setCopiedEventCode] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [regAccessCode, setRegAccessCode] = useState("");
  const [newCompetition, setNewCompetition] = useState({
    category: "Beginner",
    max_dancers: 70,
    rounds: ["Preliminary", "Quarter-Finals", "Semi-Finals", "Finals"],
    audience_voting_enabled: false,
    audience_voting_percentage: 0
  });

  const getAuthHeaders = () => ({
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  useEffect(() => {
    fetchFestival();
    fetchCompetitions();
    fetchTemplates();
    fetchPaymentInfo();
  }, [festivalId, token]);

  const fetchPaymentInfo = async () => {
    try {
      const response = await axios.get(`${API}/payment-info`);
      setPaymentInfo(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching payment info:", error);
    }
  };

  const fetchFestival = async () => {
    try {
      const response = await axios.get(`${API}/festivals/${festivalId}`, getAuthHeaders());
      setFestival(response.data);
      setRegAccessCode(response.data.registration_access_code || "");
    } catch (error) {
      toast.error("Failed to load festival");
      navigate("/dashboard");
    }
  };

  const fetchCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/competitions?festival_id=${festivalId}`, getAuthHeaders());
      setCompetitions(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching competitions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`, getAuthHeaders());
      setTemplates(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching templates:", error);
    }
  };

  const handleCreateCompetition = async (e) => {
    e.preventDefault();
    
    // Check if festival is approved
    if (festival?.status !== 'approved') {
      toast.error("Festival must be approved before creating competitions. Please submit payment first.");
      return;
    }

    const trimmedCategory = (newCompetition.category || "").trim();
    if (!trimmedCategory) {
      toast.error("Please pick a level or enter a custom category name");
      return;
    }
    
    try {
      await axios.post(`${API}/competitions`, {
        ...newCompetition,
        category: trimmedCategory,
        festival_id: festivalId
      }, getAuthHeaders());
      toast.success("Competition created successfully!");
      setShowCreateDialog(false);
      setNewCompetition({
        category: "Beginner",
        max_dancers: 70,
        rounds: ["Preliminary", "Quarter-Finals", "Semi-Finals", "Finals"],
        audience_voting_enabled: false,
        audience_voting_percentage: 0
      });
      fetchCompetitions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create competition");
    }
  };

  const handleSelectTemplate = (template) => {
    setNewCompetition({
      category: template.category,
      max_dancers: template.max_dancers,
      rounds: template.rounds,
      audience_voting_enabled: template.audience_voting_enabled,
      audience_voting_percentage: template.audience_voting_percentage
    });
    toast.success(`Template "${template.name}" applied`);
  };

  const handleSubmitPayment = async (competitionId) => {
    if (!receiptFile) {
      toast.error("Please select a receipt image");
      return;
    }

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        await axios.post(`${API}/competitions/${competitionId}/submit-payment`, {
          competition_id: competitionId,
          receipt_image_base64: base64
        }, getAuthHeaders());
        toast.success("Payment receipt submitted! Waiting for approval.");
        setShowPaymentDialog(null);
        setReceiptFile(null);
        fetchCompetitions();
      };
      reader.readAsDataURL(receiptFile);
    } catch (error) {
      toast.error("Failed to submit payment");
    }
  };

  const handleDeleteCompetition = async (compId) => {
    if (!window.confirm("Are you sure you want to delete this competition?")) return;
    try {
      await axios.delete(`${API}/competitions/${compId}`, getAuthHeaders());
      toast.success("Competition deleted");
      fetchCompetitions();
    } catch (error) {
      toast.error("Failed to delete competition");
    }
  };

  const copyIban = () => {
    if (paymentInfo?.iban) {
      navigator.clipboard.writeText(paymentInfo.iban);
      setCopiedIban(true);
      setTimeout(() => setCopiedIban(false), 2000);
      toast.success("IBAN copied to clipboard");
    }
  };

  const copyEventCode = () => {
    if (festival?.event_code) {
      navigator.clipboard.writeText(festival.event_code);
      setCopiedEventCode(true);
      setTimeout(() => setCopiedEventCode(false), 2000);
      toast.success("Event Code copied!");
    }
  };

  const handleSubmitFestivalPayment = async () => {
    if (!receiptFile) {
      toast.error("Please select a receipt image");
      return;
    }

    setSubmittingPayment(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        await axios.post(`${API}/festivals/${festivalId}/submit-payment`, {
          festival_id: festivalId,
          receipt_image_base64: base64
        }, getAuthHeaders());
        toast.success("Payment receipt submitted! Waiting for admin approval.");
        setShowFestivalPaymentDialog(false);
        setReceiptFile(null);
        fetchFestival();
      };
      reader.readAsDataURL(receiptFile);
    } catch (error) {
      toast.error("Failed to submit payment");
    } finally {
      setSubmittingPayment(false);
    }
  };

  // Logo upload handler
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error("Logo must be under 1MB");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await axios.post(`${API}/festivals/${festivalId}/logo`, { logo_base64: reader.result }, getAuthHeaders());
        toast.success("Logo uploaded!");
        fetchFestival();
      } catch (error) {
        toast.error("Failed to upload logo");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleLogoDelete = async () => {
    try {
      await axios.delete(`${API}/festivals/${festivalId}/logo`, getAuthHeaders());
      toast.success("Logo removed");
      fetchFestival();
    } catch (error) {
      toast.error("Failed to remove logo");
    }
  };

  const handleToggleSelfRegistration = async (enabled) => {
    try {
      await axios.put(`${API}/festivals/${festivalId}`, {
        name: festival.name,
        date: festival.date,
        location: festival.location,
        description: festival.description || "",
        self_registration_enabled: enabled,
        registration_access_code: festival.registration_access_code || null
      }, getAuthHeaders());
      toast.success(enabled ? "Self-registration enabled!" : "Self-registration disabled");
      fetchFestival();
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  const handleSaveAccessCode = async () => {
    try {
      await axios.put(`${API}/festivals/${festivalId}`, {
        name: festival.name,
        date: festival.date,
        location: festival.location,
        description: festival.description || "",
        self_registration_enabled: festival.self_registration_enabled,
        registration_access_code: regAccessCode || null
      }, getAuthHeaders());
      toast.success("Access code saved!");
      fetchFestival();
    } catch (error) {
      toast.error("Failed to save access code");
    }
  };

  const statusColors = {
    setup: "bg-[#A1A1AA]/20 text-[#A1A1AA]",
    pending_payment: "bg-[#F59E0B]/20 text-[#F59E0B]",
    pending_approval: "bg-[#FF007F]/20 text-[#FF007F]",
    registration: "bg-[#00E5FF]/20 text-[#00E5FF]",
    active: "bg-[#FF007F]/20 text-[#FF007F]",
    paused: "bg-[#FFD700]/20 text-[#FFD700]",
    completed: "bg-green-500/20 text-green-500",
    approved: "bg-green-500/20 text-green-500",
    rejected: "bg-red-500/20 text-red-500"
  };

  const festivalStatusBadge = () => {
    const status = festival?.status;
    if (status === 'approved') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-500">
          <CheckCircle className="w-3 h-3" />
          Approved
        </span>
      );
    } else if (status === 'pending_approval') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-[#FF007F]/20 text-[#FF007F]">
          <Clock className="w-3 h-3" />
          Pending Approval
        </span>
      );
    } else if (status === 'pending_payment') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-[#F59E0B]/20 text-[#F59E0B]">
          <AlertCircle className="w-3 h-3" />
          Payment Required
        </span>
      );
    } else if (status === 'rejected') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-500">
          <XCircle className="w-3 h-3" />
          Rejected
        </span>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-[#A1A1AA] hover:text-white" data-testid="back-btn">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div className="flex items-center gap-3">
              <Trophy className="w-8 h-8 text-[#FFD700]" />
              <span className="font-['Playfair_Display'] text-xl font-bold text-white">{festival?.name}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Festival Status & Payment Banner */}
        {festival?.status !== 'approved' && (
          <motion.div 
            className={`mb-8 p-6 rounded-xl border ${
              festival?.status === 'pending_payment' 
                ? 'bg-[#F59E0B]/10 border-[#F59E0B]/30'
                : festival?.status === 'pending_approval'
                ? 'bg-[#FF007F]/10 border-[#FF007F]/30'
                : 'bg-red-500/10 border-red-500/30'
            }`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {festival?.status === 'pending_payment' && (
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-[#F59E0B] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-white font-bold mb-1">Payment Required to Activate Festival</h3>
                    <p className="text-[#A1A1AA] text-sm">
                      Submit payment proof to activate your festival and start creating competitions.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => setShowFestivalPaymentDialog(true)}
                  className="btn-gold whitespace-nowrap"
                  data-testid="submit-festival-payment-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Submit Payment
                </Button>
              </div>
            )}
            {festival?.status === 'pending_approval' && (
              <div className="flex items-start gap-4">
                <Clock className="w-6 h-6 text-[#FF007F] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-bold mb-1">Awaiting Admin Approval</h3>
                  <p className="text-[#A1A1AA] text-sm">
                    Your payment has been submitted. An admin will review and approve your festival shortly.
                  </p>
                </div>
              </div>
            )}
            {festival?.status === 'rejected' && (
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-white font-bold mb-1">Payment Rejected</h3>
                    <p className="text-[#A1A1AA] text-sm">
                      Your payment was not approved. Please submit a valid payment receipt.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => setShowFestivalPaymentDialog(true)}
                  className="bg-red-500 hover:bg-red-600 text-white whitespace-nowrap"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Resubmit Payment
                </Button>
              </div>
            )}
          </motion.div>
        )}

        {/* Festival Info */}
        <div className="glass p-6 rounded-xl mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Playfair_Display'] text-2xl font-bold text-white">Festival Details</h2>
            {festivalStatusBadge()}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <div className="space-y-2 text-[#A1A1AA]">
                <p><strong className="text-white">Date:</strong> {festival?.date}</p>
                <p><strong className="text-white">Location:</strong> {festival?.location}</p>
                {festival?.description && <p><strong className="text-white">Description:</strong> {festival?.description}</p>}
              </div>
            </div>
            
            {/* Event Code */}
            <div className="bg-black/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Hash className="w-5 h-5 text-[#FFD700]" />
                <h3 className="text-white font-medium">Event Code</h3>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-2xl font-bold text-[#FFD700] tracking-wider">
                  {festival?.event_code}
                </span>
                <button 
                  onClick={copyEventCode}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="copy-event-code-btn"
                >
                  {copiedEventCode ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5 text-[#A1A1AA]" />}
                </button>
              </div>
              <p className="text-[#52525B] text-xs mt-2">Share this code with your judges</p>
            </div>

            {/* Quick Links */}
            {festival?.status === 'approved' && competitions.length > 0 && (
              <div className="bg-black/20 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <ExternalLink className="w-5 h-5 text-[#00E5FF]" />
                  <h3 className="text-white font-medium">Quick Links</h3>
                </div>
                <div className="space-y-2">
                  <a 
                    href={`/leaderboard/${competitions[0]?.competition_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-[#00E5FF] hover:text-[#00B8CC] text-sm"
                  >
                    Live Leaderboard
                  </a>
                  <a 
                    href="/judge-login"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-[#FF007F] hover:text-[#E5006E] text-sm"
                  >
                    Judge Login Portal
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Event Branding & Registration Settings */}
        {festival?.status === 'approved' && (
          <div className="glass p-6 rounded-xl mb-8">
            <h3 className="font-['Playfair_Display'] text-xl font-bold text-white mb-4">Event Settings</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Logo Upload */}
              <div className="bg-black/20 rounded-xl p-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Upload className="w-4 h-4 text-[#FFD700]" />
                  Event Logo
                </h4>
                {festival?.logo_base64 ? (
                  <div className="space-y-3">
                    <img src={festival.logo_base64} alt="Festival logo" className="max-h-20 rounded-lg" />
                    <div className="flex gap-2">
                      <label className="cursor-pointer">
                        <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="logo-upload-input" />
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#FFD700]/20 text-[#FFD700] rounded-lg text-xs hover:bg-[#FFD700]/30 transition-colors">
                          Change
                        </span>
                      </label>
                      <button onClick={handleLogoDelete} className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs hover:bg-red-500/30 transition-colors" data-testid="logo-delete-btn">
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="cursor-pointer block">
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="logo-upload-input" />
                    <div className="border-2 border-dashed border-white/10 rounded-xl p-6 text-center hover:border-[#FFD700]/30 transition-colors">
                      <Upload className="w-8 h-8 text-[#52525B] mx-auto mb-2" />
                      <p className="text-[#A1A1AA] text-sm">Click to upload logo</p>
                      <p className="text-[#52525B] text-xs">PNG, JPG (max 1MB)</p>
                    </div>
                  </label>
                )}
              </div>

              {/* Self-Registration */}
              <div className="bg-black/20 rounded-xl p-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#00E5FF]" />
                  Dancer Self-Registration
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm">Enable Self-Registration</p>
                      <p className="text-[#52525B] text-xs">Dancers can register themselves via a public link</p>
                    </div>
                    <Switch 
                      checked={festival?.self_registration_enabled || false}
                      onCheckedChange={handleToggleSelfRegistration}
                      data-testid="self-reg-toggle"
                    />
                  </div>
                  {festival?.self_registration_enabled && (
                    <>
                      <div>
                        <Label className="text-[#A1A1AA] text-xs">Access Code (optional)</Label>
                        <div className="flex gap-2 mt-1">
                          <Input 
                            value={regAccessCode}
                            onChange={(e) => setRegAccessCode(e.target.value)}
                            placeholder="Leave empty for open registration"
                            className="bg-black/30 border-white/10 text-white text-sm"
                            data-testid="reg-access-code-input"
                          />
                          <Button size="sm" variant="outline" className="btn-outline" onClick={handleSaveAccessCode} data-testid="save-access-code-btn">
                            Save
                          </Button>
                        </div>
                      </div>
                      <div className="p-3 bg-[#00E5FF]/5 border border-[#00E5FF]/20 rounded-lg">
                        <p className="text-[#A1A1AA] text-xs mb-1">Registration Link:</p>
                        <div className="flex items-center gap-2">
                          <code className="text-[#00E5FF] text-xs break-all flex-1">{`${window.location.origin}/event/${festival?.event_code}`}</code>
                          <button 
                            onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/event/${festival?.event_code}`); toast.success("Link copied!"); }}
                            className="p-1.5 hover:bg-white/10 rounded"
                            data-testid="copy-reg-link-btn"
                          >
                            <Copy className="w-4 h-4 text-[#A1A1AA]" />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Competitions Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <h2 className="font-['Playfair_Display'] text-2xl font-bold text-white">Competitions</h2>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button 
                className="btn-gold px-6 py-3 rounded-lg" 
                data-testid="create-competition-btn"
                disabled={festival?.status !== 'approved'}
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Competition
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Playfair_Display'] text-2xl text-white">Create Competition</DialogTitle>
              </DialogHeader>
              
              {/* Templates */}
              {templates.length > 0 && (
                <div className="mb-4">
                  <Label className="text-[#A1A1AA] mb-2 block">Quick Start with Template</Label>
                  <div className="flex flex-wrap gap-2">
                    {templates.map((t) => (
                      <Button
                        key={t.template_id}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="btn-outline text-xs"
                        onClick={() => handleSelectTemplate(t)}
                        data-testid={`template-${t.template_id}`}
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        {t.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <form onSubmit={handleCreateCompetition} className="space-y-4 mt-4">
                <div>
                  <Label className="text-[#A1A1AA]">Category / Level</Label>
                  <Select
                    value={["Beginner","Intermediate","Advanced","Open"].includes(newCompetition.category) ? newCompetition.category : "__custom__"}
                    onValueChange={(value) => {
                      if (value === "__custom__") {
                        setNewCompetition({ ...newCompetition, category: "" });
                      } else {
                        setNewCompetition({ ...newCompetition, category: value });
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1 bg-black/20 border-white/10 text-white" data-testid="category-select">
                      <SelectValue placeholder="Pick a level…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-white/10">
                      <SelectItem value="Beginner">Beginner</SelectItem>
                      <SelectItem value="Intermediate">Intermediate</SelectItem>
                      <SelectItem value="Advanced">Advanced</SelectItem>
                      <SelectItem value="Open">Open</SelectItem>
                      <SelectItem value="__custom__">Custom level…</SelectItem>
                    </SelectContent>
                  </Select>
                  {!["Beginner","Intermediate","Advanced","Open"].includes(newCompetition.category) && (
                    <Input
                      type="text"
                      value={newCompetition.category}
                      onChange={(e) => setNewCompetition({ ...newCompetition, category: e.target.value })}
                      placeholder="e.g. Pro-Am, Youth, Masters 50+, West Coast Swing"
                      className="mt-2 bg-black/20 border-white/10 text-white placeholder:text-[#52525B]"
                      maxLength={50}
                      data-testid="custom-category-input"
                    />
                  )}
                </div>
                <div>
                  <div>
                    <Label className="text-[#A1A1AA]">Max Dancers</Label>
                    <Input
                      type="number"
                      value={newCompetition.max_dancers}
                      onChange={(e) => setNewCompetition({ ...newCompetition, max_dancers: parseInt(e.target.value) })}
                      className="mt-1 bg-black/20 border-white/10 text-white"
                      min="1"
                      max="500"
                      data-testid="max-dancers-input"
                    />
                  </div>
                </div>

                {/* Audience Voting */}
                <div className="border-t border-white/10 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label className="text-white">Audience Voting</Label>
                      <p className="text-[#A1A1AA] text-xs">Allow audience to vote for dancers</p>
                    </div>
                    <Switch
                      checked={newCompetition.audience_voting_enabled}
                      onCheckedChange={(checked) => setNewCompetition({ 
                        ...newCompetition, 
                        audience_voting_enabled: checked,
                        audience_voting_percentage: checked ? 10 : 0
                      })}
                      data-testid="audience-voting-switch"
                    />
                  </div>
                  {newCompetition.audience_voting_enabled && (
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-[#A1A1AA]">Audience Score Weight</span>
                        <span className="text-[#FFD700] font-bold">{newCompetition.audience_voting_percentage}%</span>
                      </div>
                      <Slider
                        value={[newCompetition.audience_voting_percentage]}
                        onValueChange={(v) => setNewCompetition({ ...newCompetition, audience_voting_percentage: v[0] })}
                        max={50}
                        min={5}
                        step={5}
                        className="mb-2"
                        data-testid="audience-percentage-slider"
                      />
                      <p className="text-[#52525B] text-xs">
                        Judges: {100 - newCompetition.audience_voting_percentage}% | Audience: {newCompetition.audience_voting_percentage}%
                      </p>
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full btn-gold py-3" data-testid="submit-competition-btn">
                  Create Competition
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Competitions Grid */}
        {competitions.length === 0 ? (
          <div className="text-center py-16 glass rounded-xl">
            <Users className="w-12 h-12 text-[#27272a] mx-auto mb-4" />
            <h3 className="text-lg text-white mb-2">No competitions yet</h3>
            <p className="text-[#A1A1AA]">Create your first competition category</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {competitions.map((comp, index) => (
              <motion.div
                key={comp.competition_id}
                className="competition-card group"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                data-testid={`competition-card-${comp.competition_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium uppercase ${statusColors[comp.status] || statusColors.setup}`}>
                      {comp.status?.replace('_', ' ')}
                    </span>
                    <h3 className="font-['Playfair_Display'] text-xl font-bold text-white mt-2">
                      {comp.category}
                    </h3>
                  </div>
                  <button
                    onClick={() => handleDeleteCompetition(comp.competition_id)}
                    className="text-[#52525B] hover:text-red-500 transition-colors p-2"
                    data-testid={`delete-competition-${comp.competition_id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-2 text-sm text-[#A1A1AA] mb-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Max {comp.max_dancers} dancers
                  </div>
                  <div className="flex items-center gap-2">
                    <Gavel className="w-4 h-4" />
                    {comp.rounds?.length || 3} rounds
                  </div>
                  {comp.audience_voting_enabled && (
                    <div className="flex items-center gap-2 text-[#00E5FF]">
                      <Users className="w-4 h-4" />
                      Audience voting: {comp.audience_voting_percentage}%
                    </div>
                  )}
                </div>
                
                {/* Payment Button if needed */}
                {comp.status === 'pending_payment' && (
                  <Button 
                    onClick={() => setShowPaymentDialog(comp.competition_id)}
                    className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-black mb-3"
                    data-testid={`submit-payment-${comp.competition_id}`}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Submit Payment
                  </Button>
                )}
                
                <Link to={`/competition/${comp.competition_id}`} data-testid={`open-competition-${comp.competition_id}`}>
                  <Button className="w-full btn-outline py-2" disabled={!comp.is_paid && comp.status !== 'pending_approval'}>
                    {comp.is_paid ? 'Manage Competition' : 'View Details'}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {/* Payment Dialog */}
        <Dialog open={!!showPaymentDialog} onOpenChange={() => setShowPaymentDialog(null)}>
          <DialogContent className="bg-[#18181b] border-white/10 text-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display'] text-2xl text-white">Submit Payment Receipt</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="bg-black/20 p-4 rounded-lg">
                <h4 className="text-white font-medium mb-2">Payment Details</h4>
                <p className="text-[#A1A1AA] text-sm">Please transfer the competition fee to:</p>
                <div className="mt-3 space-y-1 text-sm">
                  {paymentInfo?.bank_name && <p><strong className="text-white">Bank:</strong> {paymentInfo.bank_name}</p>}
                  {paymentInfo?.iban && <p><strong className="text-white">IBAN:</strong> {paymentInfo.iban}</p>}
                  {paymentInfo?.beneficiary && <p><strong className="text-white">Beneficiary:</strong> {paymentInfo.beneficiary}</p>}
                </div>
              </div>
              
              <div>
                <Label className="text-[#A1A1AA]">Upload Payment Receipt</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setReceiptFile(e.target.files[0])}
                  className="mt-1 bg-black/20 border-white/10 text-white"
                  data-testid="receipt-file-input"
                />
                {receiptFile && (
                  <p className="text-green-500 text-sm mt-2">Selected: {receiptFile.name}</p>
                )}
              </div>
              
              <Button 
                onClick={() => handleSubmitPayment(showPaymentDialog)}
                className="w-full btn-gold py-3"
                disabled={!receiptFile}
                data-testid="confirm-payment-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Submit for Approval
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Festival Payment Dialog */}
        <Dialog open={showFestivalPaymentDialog} onOpenChange={setShowFestivalPaymentDialog}>
          <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display'] text-2xl text-white">Activate Your Festival</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 mt-4">
              {/* Payment Info Card */}
              <div className="bg-gradient-to-br from-[#FFD700]/10 to-[#FFD700]/5 p-6 rounded-xl border border-[#FFD700]/20">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5 text-[#FFD700]" />
                  <h4 className="text-white font-bold">Bank Transfer Details</h4>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-1">Beneficiary</p>
                    <p className="text-white">{paymentInfo?.beneficiary}</p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-1">IBAN</p>
                    <div className="flex items-center gap-2">
                      <p className="text-white font-mono text-sm">{paymentInfo?.iban}</p>
                      <button 
                        onClick={copyIban}
                        className="text-[#FFD700] hover:text-[#E5C100] p-1"
                        data-testid="copy-iban-dialog-btn"
                      >
                        {copiedIban ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-1">BIC/SWIFT Code</p>
                    <p className="text-white font-mono">{paymentInfo?.bic}</p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-1">Bank</p>
                    <p className="text-white">{paymentInfo?.bank_name}</p>
                  </div>
                  {paymentInfo?.correspondent_bic && (
                    <div>
                      <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-1">Correspondent Bank BIC</p>
                      <p className="text-white font-mono">{paymentInfo?.correspondent_bic}</p>
                    </div>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-[#FFD700]/20">
                  <p className="text-[#A1A1AA] text-sm">
                    <strong className="text-[#FFD700]">Reference:</strong> Include your festival name "{festival?.name}" in the payment reference
                  </p>
                </div>
              </div>

              {/* Email Instruction */}
              <div className="bg-black/20 p-4 rounded-lg">
                <p className="text-[#A1A1AA] text-sm">
                  After payment, email your receipt to: <strong className="text-white">{paymentInfo?.email}</strong>
                </p>
              </div>

              {/* Upload Receipt */}
              <div>
                <Label className="text-white mb-2 block">Upload Payment Receipt</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setReceiptFile(e.target.files[0])}
                  className="bg-black/20 border-white/10 text-white"
                  data-testid="festival-receipt-input"
                />
                {receiptFile && (
                  <p className="text-green-500 text-sm mt-2 flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    {receiptFile.name}
                  </p>
                )}
              </div>

              <Button 
                onClick={handleSubmitFestivalPayment}
                className="w-full btn-gold py-4 text-lg"
                disabled={!receiptFile || submittingPayment}
                data-testid="submit-festival-receipt-btn"
              >
                {submittingPayment ? (
                  <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Submit for Approval
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
